import React, { useEffect, useMemo, useState } from 'react';
import styled from "styled-components";
import {
  createOrUpdateOffice,
  EditableAreaEntity,
  MockEditableAreaEntity,
  OfficeEntity,
  updateSingleOfficeEntity
} from '@features/desks/services/AdvanceHotDeskingService';
import { Trans, useTranslation } from "react-i18next";
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { useDispatch, useSelector } from "react-redux";
import {
  selectFocusedChildOffice,
  selectIsLoading,
  setActiveOfficeManagementPanel,
  setFocusedChildOffice
} from '@features/desks/store/editOfficeLayout.duck';
import { OfficeManagementPanelView } from "../data/OfficeManagement.model";
import { FlatContentCard } from '@shared/ui/atoms/FlatContentCard';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import TextField from '@shared/ui/atoms/TextField';
import { Switch } from '@shared/ui/atoms/Switch';
import IconButton from '@shared/ui/molecules/icon-button/IconButton';
import { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { PendingSaveDot } from '@shared/ui/atoms/PendingSaveDot';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { selectCurrentUser } from "@shared/store/auth.duck";
import {
  fetchOfficeEntities,
  selectAllOfficeEntities
} from '@features/desks/store/advanceHotDeskingSetup.duck';
import { canUserEditOfficesInManagement } from '@shared/utils/AccessControl';
import { officeToCreateOfficeRequest } from '@shared/utils/OfficeHelper';
import { failureNotification, successNotification } from "@shared/store/notification.duck";
import { DialogIdentifiers, openDialogWithPayload } from "@shared/store/dialog.duck";
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

export function OfficeAreaManagementPanel(props: Props) {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const allOffices = useSelector(selectAllOfficeEntities);
  const loading = useSelector(selectIsLoading);
  const isEditable = useMemo(() => canUserEditOfficesInManagement(currentUser), [currentUser]);
  const parentOffice = useSelector(selectFocusedChildOffice);

  const [areas, setAreas] = useState<EditableAreaEntity[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const goBackToDeskManagement = () => {
    dispatch(setFocusedChildOffice(undefined));
    dispatch(setActiveOfficeManagementPanel(OfficeManagementPanelView.DeskManagement));
  }

  useEffect(() => {
    if (parentOffice) {
      setAreas(allOffices.filter((area: OfficeEntity) => area.parentId === parentOffice.id && area.area));
    }
  }, [allOffices, parentOffice]);

  const updateArea = (label: string, capacity: number, active: boolean, area: EditableAreaEntity) => {
    const idx = areas.indexOf(area);
    if (idx > -1) {
      const updatedAreas = [...areas];
      updatedAreas.splice(idx, 1, {...area, label: label, capacity: capacity, active: active, dirty: true});
      setAreas(updatedAreas);
      setIsDirty(true);
    }
  }

  const addNewArea = () => {
    if (parentOffice) {
      const newArea = {
        ...MockEditableAreaEntity,
        parentId: parentOffice.id,
        companyId: currentUser?.companyEntity.id,
        dirty: true,
      } as any;
      setAreas([...areas, newArea]);
      setIsDirty(true);
    }
  }

  const saveChanges = async () => {
    setIsSaving(true);
    try {
      await Promise.all(
        areas.filter(area => area.dirty)
          .map(area => area.id ? updateSingleOfficeEntity(area) : createOrUpdateOffice(officeToCreateOfficeRequest(area)))
      );
      dispatch(fetchOfficeEntities());
      setIsDirty(false);
      dispatch(successNotification('Successfully updated all areas'));
    } catch (e: any) {
      dispatch(failureNotification('Failed to update one or more areas.'));
    } finally {
      setIsSaving(false);
    }
  }

  const onDeleteClicked = (area: EditableAreaEntity) => {
    if (area.id) {
      dispatch(openDialogWithPayload({
        payload: { officeId: area.id },
        activeDialog: DialogIdentifiers.DeleteOfficeEntityDialog
      }))
    }
  }

  return (
    <Container>
      <GoBackButton onClick={goBackToDeskManagement} />
      <HeadlineMedium style={{marginBottom: 24}}>{t('settings.bookable-areas')}</HeadlineMedium>

      {parentOffice?.floorPlanEnabled && <>
        <FlatContentCard style={{marginBottom: 24}}>
          <BodyRegular style={{marginBottom: 8}} weight={600}>{t('settings.manage-desk.floor-plans-enabled')}</BodyRegular>
          <BodyRegular><Trans i18nKey={'settings.manage-desk.contact-us-to-change'}
                              components={[<a href="mailto:hello@team-today.com">hello@team-today.com</a>]} /></BodyRegular>
        </FlatContentCard>
      </>}

      {loading ? <>
        <Center>
          <LoadingSpinner hideBorder={true} />
        </Center>
      </> : <>
        {areas.map((area: EditableAreaEntity, key: number) => <div key={key} className={"manageAreas__areaRow"}>
          <ManageDeskRow>
            <NameColumn>
              <BodyRegular weight={600}>{t('settings.manage-area.name')}</BodyRegular>
              <TextField onChange={(val: string) => updateArea(val, area.capacity, area.active, area)}
                         value={area.label}
                         dataTest={"area-label"}
                         fullWidth={true} />
            </NameColumn>
            <ControlsColumn>
              <BodyRegular weight={600}>{t('settings.manage-desk.number-of-desks')}</BodyRegular>
              <ControlsRow>
                <TextFieldSpacesContainer>
                  <TextField onChange={(val: string) => updateArea(area.label, parseInt(val), area.active, area)}
                             value={`${area.capacity}`}
                             type={'number'}
                             dataTest={"area-capacity"}
                             fullWidth={true} />
                </TextFieldSpacesContainer>

                <Switch value={area.active}
                        label={""}
                        onChange={enabled => updateArea(area.label, area.capacity, enabled, area)} />

                {!!area.id && <div style={{marginLeft: 12}}>
                  <IconButton icon={IconTypes.Bin} onClick={() => onDeleteClicked(area)} />
                </div>}


                <PendingSaveDot visible={!!area.dirty} style={{marginLeft: 16}} />

              </ControlsRow>
            </ControlsColumn>
          </ManageDeskRow>
        </div>)}
      </>}



      <OutlineButton click={addNewArea}
                     text={"button.add-new-area"}
                     dataTest={"add-new-area-button"}
                     size={ButtonSize.Medium}
                     fullWidth={true}
                     style={{marginBottom: 16, marginTop: 32}}
                     disabled={!isEditable || isSaving || loading} />

      <PrimaryButton click={saveChanges}
                     text={"button.save-changes"}
                     dataTest={"save-areas-button"}
                     fullWidth={true}
                     size={ButtonSize.Large}
                     disabled={(isEditable && !isDirty) || isSaving || loading} />

    </Container>
  )
}

interface Props {
  office: OfficeEntity;
}

const Container = styled.div`
  
`

const ManageDeskRow = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 20px;
`
const NameColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding-right: 20px;
`
const ControlsColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 40%;
`
const ControlsRow = styled.div`
  display: flex;
  justify-content: space-between;
  .iconButton--mediumlarge {
    padding: 0;
    margin: 0;
  }
`
const TextFieldSpacesContainer = styled.div`
  display: flex;
  width: 80px;
  justify-content: center;
  .textField {
    margin: 0;
  }
`
